import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, ShieldX, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY = { venue: "binance", symbol: "BTCUSDT", side: "BUY", qty: "0.01", order_type: "MARKET", limit_price: "", stop_loss: "", take_profit: "" };

function Seg({ value, options, onChange, testId }) {
  return (
    <div className="flex rounded-md border border-[var(--border-subtle)] overflow-hidden" data-testid={testId}>
      {options.map(([v, l]) => (
        <button key={v} type="button" data-testid={`${testId}-${v}`} onClick={() => onChange(v)}
          className={cn("flex-1 px-3 py-1.5 text-xs font-medium transition-colors", value === v ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-[var(--card-hover)]")}>{l}</button>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;
}

export function RiskChecks({ result }) {
  if (!result) return null;
  return (
    <div data-testid="risk-check-results" className="mt-3 rounded-md border border-[var(--border-subtle)] p-3 space-y-1">
      <div className={cn("text-xs font-semibold flex items-center gap-1.5", result.allowed ? "text-emerald-400" : "text-rose-400")}>
        {result.allowed ? <ShieldCheck className="w-4 h-4" /> : <ShieldX className="w-4 h-4" />}
        {result.allowed ? `All checks passed · price ${Number(result.price).toLocaleString("en-US", { maximumFractionDigits: 6 })}` : "Blocked by risk controls"}
      </div>
      {(result.checks || []).map((c) => (
        <div key={c.rule} className="flex items-start gap-1.5 text-[11px]" data-testid={`check-${c.rule}`}>
          {c.passed ? <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /> : <XCircle className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" />}
          <span className="text-slate-300 font-mono">{c.rule}</span><span className="text-slate-500">— {c.detail}</span>
        </div>
      ))}
    </div>
  );
}

export function OrderTicket({ disabled }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); setResult(null); };
  const payload = () => ({ ...f, client_order_id: undefined });
  const preview = useMutation({
    mutationFn: async () => (await api.post("/paper/orders/preview", payload())).data,
    onSuccess: setResult, onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const place = useMutation({
    mutationFn: async () => (await api.post("/paper/orders", { ...payload(), client_order_id: crypto.randomUUID() })).data,
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: ["paper"] });
      if (o.status === "REJECTED") { setResult({ allowed: false, checks: o.risk_checks }); toast.error(`Rejected: ${o.reject_reason}`); }
      else { setResult(null); toast.success(`Paper order ${o.status}: ${o.side} ${o.qty} ${o.symbol}`); }
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const venueChange = (v) => setF((p) => ({ ...p, venue: v, symbol: v === "binance" ? "BTCUSDT" : "RELIANCE", qty: v === "binance" ? "0.01" : "10" }));
  return (
    <Panel title="Paper Order Ticket" testId="order-ticket">
      <div className="p-4 space-y-3">
        <Seg testId="ticket-venue" value={f.venue} onChange={venueChange} options={[["binance", "Binance · crypto"], ["sharekhan", "Sharekhan · stocks (simulated px)"]]} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Symbol"><Input data-testid="ticket-symbol" value={f.symbol} onChange={(e) => set("symbol")(e.target.value.toUpperCase())} className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono" /></Field>
          <Field label="Quantity"><Input data-testid="ticket-qty" value={f.qty} onChange={(e) => set("qty")(e.target.value)} className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono" /></Field>
        </div>
        <Seg testId="ticket-side" value={f.side} onChange={set("side")} options={[["BUY", "Buy"], ["SELL", "Sell (close)"]]} />
        <Seg testId="ticket-type" value={f.order_type} onChange={set("order_type")} options={[["MARKET", "Market"], ["LIMIT", "Limit"]]} />
        <div className="grid grid-cols-3 gap-2">
          <Field label="Limit"><Input data-testid="ticket-limit" disabled={f.order_type !== "LIMIT"} value={f.limit_price} onChange={(e) => set("limit_price")(e.target.value)} className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono" /></Field>
          <Field label="Stop-loss"><Input data-testid="ticket-stop" value={f.stop_loss} onChange={(e) => set("stop_loss")(e.target.value)} className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono" /></Field>
          <Field label="Target"><Input data-testid="ticket-target" value={f.take_profit} onChange={(e) => set("take_profit")(e.target.value)} className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] font-mono" /></Field>
        </div>
        <div className="flex gap-2">
          <Button data-testid="ticket-check-btn" variant="outline" onClick={() => preview.mutate()} disabled={preview.isPending} className="flex-1 border-[var(--border-subtle)]">Check risk</Button>
          <Button data-testid="ticket-submit-btn" onClick={() => place.mutate()} disabled={disabled || place.isPending} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white">Place paper order</Button>
        </div>
        {disabled && <div className="text-[11px] text-rose-400" data-testid="ticket-kill-note">Kill switch engaged — new orders are blocked.</div>}
        <RiskChecks result={result} />
      </div>
    </Panel>
  );
}
